import { trackUserEvent } from '@vend/utilities'
import { EffectsMap } from 'use-effect-reducer'
import { ApiTransaction } from '@vend/types'
import { WEB_REGISTER } from '../../../../web-register.constant'
import { connectToTerminal } from '../../helpers/connectToTerminal'
import { isCancellation } from '../../helpers/errorChecking'
import {
  LSPAYPairingData,
  LSPAYPairingRequest,
  SwiperError,
} from '../../helpers/lspay.types'
import {
  FAILED_INTERACT,
  FAILED_PAIRING_CHECK,
  FAILED_REFUND,
  FAILED_TERMINAL_CONNECTION,
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  PAIRING_ERROR,
  SIMULATOR_SN,
  STRIPE_ERROR_CODE,
  SWIPER_ERROR_CODES,
} from '../../helpers/lspayConstants'
import {
  readReusableCard,
  refund,
  refundWithTerminal,
} from '../../helpers/refund'
import {
  collectSetupIntent,
  createSetupIntent,
} from '../../helpers/setupIntent'
import { GET_PAIRING_DATA } from '../../queries/Pairings'
import { getConnectionErrorMessage } from '../../helpers/getConnectionErrorMessage'
import { persistRefund } from './helpers'
import {
  RefundEffect,
  RefundEffectsMapArgs,
  RefundEvent,
  RefundState,
} from './types'

export const getEffectForRetry = (
  state: RefundState
): RefundEffect | undefined => {
  switch (state.status) {
    case 'refundingViaCardNotPresent': {
      return {
        type: 'cardNotPresentEffect',
      }
    }
    case 'refundingViaCardPresent': {
      return {
        type: 'cardPresentEffect',
        chargeId: state.chargeId,
        terminal: state.terminal,
        terminalMonitoringInstance: state.terminalMonitoringInstance,
        amount: state.amount,
        isRetry: true,
      }
    }
    case 'fetchingPairing': {
      return {
        type: 'fetchPairingEffect',
      }
    }
    case 'connectingToTerminal': {
      return {
        type: 'connectTerminalEffect',
        pairingSN: state.pairing.sn,
        isSimulated: state.pairing.isSimulated,
        terminalID: state.pairing.terminalID,
        terminalModel: state.pairing.terminalModel,
      }
    }
    case 'persistingCardPresentRefund': {
      return {
        type: 'persistCardPresentEffect',
        refundMethod: state.refundMethod,
        amount: state.amount,
      }
    }
    case 'readingReusableCard': {
      return {
        type: 'readReusableCardEffect',
        terminal: state.terminal,
        terminalMonitoringInstance: state.terminalMonitoringInstance,
        isRetry: true,
      }
    }
    case 'performingUnreferencedRefund': {
      return {
        type: 'unreferencedRefundEffect',
        paymentMethodId: state.paymentMethodId,
        amount: state.amount,
      }
    }
    case 'collectingSetupIntentPaymentMethod': {
      return {
        type: 'collectSetupIntentPaymentMethodEffect',
        terminal: state.terminal,
        terminalMonitoringInstance: state.terminalMonitoringInstance,
        clientSecret: state.clientSecret,
        isRetry: false,
      }
    }
    case 'creatingSetupIntent': {
      return {
        type: 'createSetupIntentEffect',
      }
    }
  }
}

const getOriginalRegisterSalePaymentId = (
  originalRegisterSalePaymentIds: string[]
): string | null => {
  if (originalRegisterSalePaymentIds.length === 1) {
    return originalRegisterSalePaymentIds[0]
  }

  return null
}

export const getRefundEffectsMap = (
  args: RefundEffectsMapArgs
): EffectsMap<RefundState, RefundEvent, RefundEffect> => {
  const {
    currency,
    outletId,
    originalRegisterSalePaymentIds,
    transactionId,
    retailerPaymentType,
    register,
    trackError,
    trackTiming,
    isSimulated,
    apolloClient,
    isSandbox,
  } = args

  const { id: registerId, name: registerName } = register

  const originalRegisterSalePaymentId = originalRegisterSalePaymentIds
    ? getOriginalRegisterSalePaymentId(originalRegisterSalePaymentIds)
    : null

  return {
    cardNotPresentEffect: (_state, effect, dispatch) => {
      const refundAmount = effect.overrideAmount || args.amount

      refund({
        registerID: registerId,
        registerName,
        currency,
        retailerPaymentTypeID: retailerPaymentType.id,
        registerSalePaymentID: transactionId,
        outletId,
        amount: refundAmount,
        trackTiming,
        originalRegisterSalePaymentId,
      })
        .then(transaction => {
          dispatch({ type: 'COMPLETE_REFUND', transaction })
        })
        .catch((error: SwiperError) => {
          dispatch({
            type: 'CARD_NOT_PRESENT_FAIL',
            error,
            registerId,
            amount: refundAmount,
          })

          // If its an interac error just send a UBE
          if (
            error.code ===
            SWIPER_ERROR_CODES.LSPAY_CARD_NOT_PRESENT_INTERAC_REFUND
          ) {
            trackUserEvent(WEB_REGISTER.DISPLAY_NAME, FAILED_INTERACT, {
              error,
              transactionId,
              originalRegisterSalePaymentId,
            })
            return
          }

          // else we track an error
          trackError(FAILED_REFUND, error)
        })
    },
    fetchPairingEffect: (_state, _effect, dispatch) => {
      if (isSimulated) {
        dispatch({
          type: 'PAIRING_OK',
          pairing: {
            sn: SIMULATOR_SN,
            label: 'simulator',
            isSimulated,
          },
        })
        return
      }

      apolloClient
        .query<LSPAYPairingData, LSPAYPairingRequest>({
          query: GET_PAIRING_DATA,
          variables: {
            registerID: registerId,
          },
        })
        .then(result => {
          const pairing = result.data.lspayPairings[0]
          if (pairing) {
            const { deviceSN, deviceLabel, deviceID, deviceType } = pairing
            dispatch({
              type: 'PAIRING_OK',
              pairing: {
                sn: deviceSN,
                label: deviceLabel,
                isSimulated: false,
                terminalID: deviceID,
                terminalModel: deviceType,
              },
            })

            return
          }

          // there was no pairing dispatch an error
          dispatch({
            type: 'ERROR',
            errorMessage:
              LSPAY_ERROR_MESSAGES[LSPAY_ERROR.PAIRING_MISSING_REFUND],
            retryable: false,
          })
        })
        .catch(error => {
          // Distinguish network errors (HTTP 4xx or 5xx, or a different error code) from a missing pairing
          const errorCode = error?.graphQLErrors?.[0]?.extensions?.code
          const pairingIsMissing =
            errorCode === SWIPER_ERROR_CODES.CODE_REGISTER_PAIRING_NOT_FOUND
          const errorMessage = pairingIsMissing
            ? LSPAY_ERROR_MESSAGES[LSPAY_ERROR.PAIRING_MISSING_REFUND]
            : LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND]
          dispatch({
            type: 'ERROR',
            errorMessage,
            retryable: !pairingIsMissing,
          })
          if (!pairingIsMissing) {
            trackError(FAILED_PAIRING_CHECK, error)
          }
        })
    },
    connectTerminalEffect: (state, effect, dispatch) => {
      connectToTerminal(
        {
          outletId,
          registerId,
          pairingSN: effect.pairingSN,
          isSimulated: effect.isSimulated,
          terminalID: effect.terminalID,
          terminalModel: effect.terminalModel,
        },
        isSandbox,
        trackTiming,
        true
      )
        .then(({ terminal, terminalMonitoringInstance }) => {
          dispatch({
            type: 'TERMINAL_OK',
            terminal,
            terminalMonitoringInstance,
            currency,
          })
        })
        .catch(error => {
          if (state.status === 'connectingToTerminal') {
            const code = error.code || error.error?.code || error.name
            const retryable =
              code !== STRIPE_ERROR_CODE.FAILED_FETCH_CONNECTION_TOKEN &&
              code !== PAIRING_ERROR

            dispatch({
              type: 'ERROR',
              errorMessage: getConnectionErrorMessage({
                error,
                pairingLabel: state.pairing.label || 'no label',
                paymentTypeName: state.retailerPaymentType.name,
                isRefund: true,
              }),
              retryable,
            })
          }

          trackError(FAILED_TERMINAL_CONNECTION, error, undefined, [
            STRIPE_ERROR_CODE.READER_ERROR,
          ])
        })
    },
    readReusableCardEffect: (_state, effect, dispatch) => {
      const { terminal, terminalMonitoringInstance, isRetry } = effect
      readReusableCard(terminal, terminalMonitoringInstance, isRetry)
        .then(paymentMethodId => {
          dispatch({ type: 'PAYMENT_METHOD_OK', paymentMethodId })
        })
        .catch(error => {
          const errorMessage = isCancellation(error)
            ? LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CANCEL_REFUND]
            : LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND]
          dispatch({ type: 'ERROR', errorMessage, retryable: true })

          // if its a cancellation record a UBE
          if (isCancellation(error)) {
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              'LSPAY Read reusable card cancelled',
              {
                error,
                transactionId,
                originalRegisterSalePaymentIds,
              }
            )
            return
          }

          // otherwise record an error
          trackError('[lspay] Error reading reusable card', error)
        })
    },
    createSetupIntentEffect: (_state, _effect, dispatch) => {
      createSetupIntent(retailerPaymentType.id, outletId)
        .then(clientSecret => {
          dispatch({
            type: 'CREATE_SETUP_INTENT_PAYMENT_METHOD_OK',
            clientSecret,
          })
        })
        .catch(error => {
          trackError('[lspay] Error creating setup intent.', error)

          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND],
            retryable: true,
          })
        })
    },
    collectSetupIntentPaymentMethodEffect: (_state, effect, dispatch) => {
      const {
        terminal,
        terminalMonitoringInstance,
        clientSecret,
        isRetry,
      } = effect
      collectSetupIntent(
        terminal,
        terminalMonitoringInstance,
        clientSecret,
        isRetry
      )
        .then(paymentMethodId => {
          dispatch({ type: 'PAYMENT_METHOD_OK', paymentMethodId })
        })
        .catch(error => {
          const errorMessage = isCancellation(error)
            ? LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CANCEL_REFUND]
            : LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND]
          dispatch({ type: 'ERROR', errorMessage, retryable: true })
          // if its a cancellation send a UBE
          if (isCancellation(error)) {
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              'LSPAY collect setup payment method cancelled',
              {
                error,
                transactionId,
              }
            )
            return
          }
          trackError('[lspay] Error collecting setup payment method.', error)
        })
    },
    unreferencedRefundEffect: (_state, effect, dispatch) => {
      const { paymentMethodId } = effect

      refund({
        registerID: registerId,
        registerName,
        currency,
        retailerPaymentTypeID: retailerPaymentType.id,
        registerSalePaymentID: transactionId,
        outletId,
        amount: effect.amount,
        trackTiming,
        originalRegisterSalePaymentId,
        refundMethod: { type: 'unreferenced' },
        unreferencedPaymentMethodID: paymentMethodId,
      })
        .then(transaction => {
          dispatch({ type: 'COMPLETE_REFUND', transaction })
        })
        .catch(error => {
          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND],
            retryable: true,
          })
          trackError('[lspay] Error performing unreferenced refund.', error, {
            paymentMethodId,
          })
        })
    },
    cardPresentEffect: (_state, effect, dispatch) => {
      const {
        chargeId,
        amount,
        terminal,
        terminalMonitoringInstance,
        isRetry,
      } = effect
      refundWithTerminal(
        chargeId,
        amount,
        currency,
        terminal,
        terminalMonitoringInstance,
        trackTiming,
        isRetry
      )
        .then(() => {
          dispatch({ type: 'CARD_PRESENT_OK' })
        })
        .catch(error => {
          const errorMessage = isCancellation(error)
            ? LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CANCEL_REFUND]
            : LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND]
          dispatch({ type: 'ERROR', errorMessage, retryable: true })
          // if its a cancellation record a UBE
          if (isCancellation(error)) {
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              'LSPAY Card present refund cancelled',
              {
                error,
                transactionId,
                originalRegisterSalePaymentIds,
              }
            )
            return
          }

          // otherwise record an error
          trackError('[lspay] card present refund processing error', error, {
            chargeId,
          })
        })
    },
    persistCardPresentEffect: (_state, effect, dispatch) => {
      persistRefund({
        registerID: registerId,
        registerName,
        currency,
        retailerPaymentTypeID: retailerPaymentType.id,
        registerSalePaymentID: transactionId,
        outletId,
        amount: effect.amount,
        trackTiming,
        originalRegisterSalePaymentId,
        refundMethod: effect.refundMethod,
      })
        .then(transaction => dispatch({ type: 'COMPLETE_REFUND', transaction }))
        .catch(error => {
          // Create a transaction which is as similar to the one returned from swiper as possible.
          const transaction: ApiTransaction = {
            id: '', // we don't have a swiper tender id as this was generated locally
            register_sale_payment_id: transactionId,
            tenders: [
              {
                id: '', // we don't have a swiper tender id as this was generated locally
                external_transaction_id: '', // currently swiper returns this empty so we can leave it empty here
                currency,
                amount: effect.amount.abs().toNumber(),
                emv: {
                  tags: null,
                },
                card_details: {
                  card: {},
                },
                type: 'REFUND',
                status: 'REFUNDED',
                provider_fields: {
                  lspay_refund_method: 'interac',
                  lspay_note: 'not stored in swiper',
                },
                created_at: new Date().toISOString(),
              },
            ],
            register_id: registerId,
            retailer_payment_type_id: retailerPaymentType.id,
            payment_type_id: retailerPaymentType.type_id,
            status: 'REFUNDED',
            provider_fields: {},
          }

          trackError(
            '[lspay] There was an error saving this refund in swiper. Using the fallback mechanism.',
            error,
            { transaction }
          )

          dispatch({ type: 'COMPLETE_REFUND', transaction })
        })
    },
  }
}
